import { useCallback, useEffect, useMemo, useRef } from 'react';
import { gsap } from 'gsap';
import { useLanguage } from '../../i18n';
import { usePrefersReducedMotion } from '../../hooks/useMediaQuery';
import './ProjectModal.scss';

const IMAGE_DISTRIBUTIONS = {
  1: { rows: [1], pattern: 'single' },
  2: { rows: [2], pattern: 'duo' },
  3: { rows: [3], pattern: 'trio' },
  4: { rows: [2, 2], pattern: 'quad' },
  5: { rows: [2, 3], pattern: 'penta' },
  6: { rows: [3, 3], pattern: 'hexa' },
  7: { rows: [4, 3], pattern: 'septa' },
  8: { rows: [3, 2, 3], pattern: 'octa' },
};

const getDistribution = (count) =>
  IMAGE_DISTRIBUTIONS[count] || { rows: [3, 3, 3], pattern: 'grid' };

const chunkIntoRows = (images, rowSizes) => {
  const rows = [];
  let cursor = 0;
  for (const size of rowSizes) {
    if (cursor >= images.length) break;
    rows.push(images.slice(cursor, cursor + size));
    cursor += size;
  }
  return rows;
};

const ProjectModal = ({ project, isOpen, onClose }) => {
  const { t } = useLanguage();
  const reduceMotion = usePrefersReducedMotion();
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const distribution = useMemo(() => {
    const gallery = project?.galleryImages ?? [];
    return { dist: getDistribution(gallery.length), gallery };
  }, [project]);

  const imageRows = useMemo(
    () => chunkIntoRows(distribution.gallery, distribution.dist.rows),
    [distribution],
  );

  useEffect(() => {
    if (!isOpen || !project) return undefined;

    previouslyFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const modal = modalRef.current;
    if (modal) {
      gsap.set(modal, { display: 'flex' });
      if (reduceMotion) {
        gsap.set(modal, { opacity: 1 });
        gsap.set(contentRef.current, { opacity: 1, scale: 1 });
        gsap.set('.project-modal__image-wrapper', { opacity: 1, scale: 1 });
        gsap.set('.project-modal__info > *', { opacity: 1, x: 0 });
      } else {
        const tl = gsap.timeline();
        tl.fromTo(modal, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' })
          .fromTo(
            contentRef.current,
            { scale: 0.9, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.4, ease: 'power3.out' },
            '-=0.1',
          )
          .fromTo(
            '.project-modal__image-wrapper',
            { scale: 1.1, opacity: 0 },
            { scale: 1, opacity: 1, duration: 0.5, stagger: 0.05, ease: 'power3.out' },
            '-=0.2',
          )
          .fromTo(
            '.project-modal__info > *',
            { x: 50, opacity: 0 },
            { x: 0, opacity: 1, duration: 0.4, stagger: 0.1, ease: 'power3.out' },
            '-=0.3',
          );
      }
    }

    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen, project, reduceMotion]);

  const runCloseAnimation = useCallback(() => {
    if (reduceMotion) {
      gsap.set(modalRef.current, { display: 'none', opacity: 0 });
      onClose();
      return;
    }

    const tl = gsap.timeline({ onComplete: onClose });
    tl.to(contentRef.current, { scale: 0.9, opacity: 0, duration: 0.3 })
      .to(modalRef.current, { opacity: 0, duration: 0.3 }, '-=0.2')
      .set(modalRef.current, { display: 'none' });
  }, [onClose, reduceMotion]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        runCloseAnimation();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, runCloseAnimation]);

  const handleBackdropClick = (event) => {
    if (event.target === modalRef.current) runCloseAnimation();
  };

  if (!project) return null;

  const titleId = `project-modal-title-${project.id}`;

  return (
    <div
      className="project-modal"
      ref={modalRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-hidden={!isOpen}
    >
      <div className="project-modal__content" ref={contentRef}>
        <button
          type="button"
          className="project-modal__close"
          onClick={runCloseAnimation}
          aria-label={t('common.close')}
          ref={closeButtonRef}
        >
          ×
        </button>

        <div className="project-modal__container">
          <div
            className="project-modal__left"
            style={{ backgroundColor: project.brandColor || '#E8D9EC' }}
          >
            <div
              className={`project-modal__images pattern-${distribution.dist.pattern}`}
              data-count={distribution.gallery.length}
            >
              {imageRows.map((row, rowIndex) => (
                <div
                  key={`row-${rowIndex}`}
                  className="project-modal__row"
                  data-images={row.length}
                >
                  {row.map((image, index) => (
                    <div
                      key={`image-${rowIndex}-${index}`}
                      className="project-modal__image-wrapper"
                    >
                      <img
                        src={image}
                        alt={`${project.name}`}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="project-modal__right">
            <div className="project-modal__info">
              <div className="project-modal__logo">
                <img
                  src={project.logo}
                  alt={project.name}
                  className="project-modal__company-logo"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="project-modal__description">
                <h3 id={titleId} className="visually-hidden">
                  {project.name}
                </h3>
                <p>{project.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
